import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FurnitureData } from '../../services/furniture';
import ImageUpload from '../common/ImageUpload';
import { NEIGHBORHOODS } from '../../constants/neighborhoods';
import { useTranslation } from 'react-i18next';

// Form state: server assigns _id on create, so it's optional here
export type FurnitureFormData = Omit<FurnitureData, '_id'> & { _id?: string };

interface FurnitureFormProps {
  initialData?: Partial<FurnitureData>;
  onSubmit: (data: FurnitureFormData) => Promise<void>;
  isEditing?: boolean;
}

// Canonical enums (LAUNCH.md §2)
const CATEGORIES = [
  'sofa',
  'table',
  'chair',
  'desk',
  'bed',
  'dresser',
  'storage',
  'lighting',
  'other'
];

const CONDITIONS = [
  'like-new',
  'good',
  'fair',
  'needs-repair'
];

const FurnitureForm: React.FC<FurnitureFormProps> = ({
  initialData = {},
  onSubmit,
  isEditing = false
}) => {
  const [formData, setFormData] = useState<FurnitureFormData>({
    title: '',
    description: '',
    category: 'other',
    condition: 'good',
    price: 0,
    images: [],
    location: {
      address: '',
      city: '',
      state: 'MA',
      zipCode: ''
    },
    deliveryAvailable: false,
    neighborhood: '',
    ...initialData
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(isEditing);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof FurnitureFormData] as object),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? Number(value) : value
      }));
    }
  };

  const handleImagesUploaded = (urls: string[]) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...urls]
    }));
  };

  const handleRemoveImage = (urlToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(url => url !== urlToRemove)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!isEditing && !agreedToTerms) {
      setError(t('furniture.form.agreementRequired'));
      setLoading(false);
      return;
    }

    try {
      await onSubmit(formData);
      navigate('/seller/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || t('furniture.form.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('furniture.form.title')}</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('furniture.form.description')}</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
          <label className="block text-sm font-medium text-gray-700">{t('furniture.form.category')}</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {t(`furniture.sell.form.${category}`)}
                </option>
              ))}
            </select>
          </div>

          <div>
          <label className="block text-sm font-medium text-gray-700">{t('furniture.form.condition')}</label>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {CONDITIONS.map(condition => (
                <option key={condition} value={condition}>
                  {t(`furniture.sell.form.${condition === 'like-new' ? 'likeNew' : condition === 'needs-repair' ? 'needsRepair' : condition}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('furniture.form.price')}</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('furniture.form.originalPrice')}</label>
            <input
              type="number"
              name="originalPrice"
              value={formData.originalPrice || ''}
              onChange={handleChange}
              min="0"
              step="0.01"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700">{t('furniture.form.location')}</h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('furniture.form.address')}</label>
              <input
                type="text"
                name="location.address"
                value={formData.location.address || ''}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('furniture.form.city')}</label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('furniture.form.state')}</label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('furniture.form.zip')}</label>
                <input
                  type="text"
                  name="location.zipCode"
                  value={formData.location.zipCode || ''}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('furniture.form.neighborhood')}</label>
          <select
            name="neighborhood"
            value={formData.neighborhood || ''}
            onChange={handleChange}
            required={!isEditing}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">{t('furniture.form.selectNeighborhood')}</option>
            {NEIGHBORHOODS.map((item) => (
              <option key={item.slug} value={item.slug}>{i18n.language.startsWith('zh') ? item.zh : item.en}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('furniture.form.images')}
          </label>
          <ImageUpload
            onImagesUploaded={handleImagesUploaded}
            existingImages={formData.images}
            onRemoveImage={handleRemoveImage}
            maxFiles={5}
          />
        </div>

        {!isEditing && (
          <label className="flex items-start text-sm text-gray-700">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
            <span className="ml-2">
              {t('furniture.form.agreementPrefix')}{' '}
              <Link to="/terms" className="text-indigo-600 underline">{t('furniture.sell.form.agreementTerms')}</Link>
              {' '}{t('furniture.sell.form.agreementAnd')}{' '}
              <Link to="/disclaimer" className="text-indigo-600 underline">{t('furniture.sell.form.agreementDisclaimer')}</Link>
            </span>
          </label>
        )}
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('furniture.form.cancel')}
        </button>
        <button
          type="submit"
          disabled={loading || (!isEditing && !agreedToTerms)}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('furniture.form.saving') : isEditing ? t('furniture.form.update') : t('furniture.form.create')}
        </button>
      </div>
    </form>
  );
};

export default FurnitureForm;
